import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Fade} from '@mui/material';
import type {FadeProps} from '@mui/material/Fade';
import {flushSync} from 'react-dom';
import {useHistory, useLocation} from 'react-router-dom';
import {isCourseManagementRouteReady} from './courseManagementRouteLoaders';
import './courseManagementMotion.css';

export type CourseMotionKind = 'route' | 'layout' | 'presence' | 'results';

type MotionRegionRef = {readonly current: HTMLElement | null};

type CourseMotionOptions = {
  kind: CourseMotionKind;
  regions?: MotionRegionRef[];
};

type NavigateOptions = {
  replace?: boolean;
};

type BrowserViewTransition = {
  finished: Promise<void>;
  skipTransition: () => void;
};

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => BrowserViewTransition;
};

type CourseManagementMotionContextValue = {
  animateRouteEntry: (pathname: string, region: MotionRegionRef) => void;
  cancelMotion: () => void;
  enabled: boolean;
  navigateWithMotion: (destination: string, options?: NavigateOptions) => void;
  reducedMotion: boolean;
  runMotion: (commit: () => void, options: CourseMotionOptions) => void;
};

const MOTION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const MOTION_DURATION: Record<CourseMotionKind, number> = {
  route: 280,
  layout: 280,
  presence: 200,
  results: 160,
};
const MAX_VIEW_TRANSITION_REGIONS = 2;
const DIALOG_DURATION = {enter: 220, exit: 180} as const;
const DIALOG_BACKDROP_DURATION = {enter: 180, exit: 150} as const;
const DIALOG_HIDDEN_TRANSFORM = 'translate3d(0, 8px, 0) scale(0.985)';
const DIALOG_VISIBLE_TRANSFORM = 'translate3d(0, 0, 0) scale(1)';

const immediateMotionContext: CourseManagementMotionContextValue = {
  animateRouteEntry: () => undefined,
  cancelMotion: () => undefined,
  enabled: false,
  navigateWithMotion: () => undefined,
  reducedMotion: false,
  runMotion: commit => commit(),
};

const CourseManagementMotionContext = createContext<CourseManagementMotionContextValue>(immediateMotionContext);

const uniqueElements = (regions: MotionRegionRef[] = []) => Array.from(new Set(
  regions.map(region => region.current).filter((element): element is HTMLElement => Boolean(element)),
));

const getTranslate = (before: DOMRect, after: DOMRect) => ({
  x: before.left - after.left,
  y: before.top - after.top,
});

const getDialogMotionTarget = (node: HTMLElement) => (
  node.querySelector<HTMLElement>('.MuiDialog-paper') ?? node
);

const setDialogTransformTransition = (node: HTMLElement, duration: number) => {
  node.style.transition = `transform ${duration}ms ${MOTION_EASING}`;
};

const clearDialogTransformStyles = (node: HTMLElement) => {
  node.style.willChange = '';
  node.style.transition = '';
  node.style.transform = '';
};

export const CourseDialogTransition = React.forwardRef<unknown, FadeProps>(function CourseDialogTransition(
  {onEnter, onEntering, onEntered, onExit, onExited, ...props},
  ref,
) {
  return (
    <Fade
      ref={ref}
      {...props}
      onEnter={(node, isAppearing) => {
        const target = getDialogMotionTarget(node);
        target.style.transform = DIALOG_HIDDEN_TRANSFORM;
        target.style.willChange = 'transform';
        // Flush the initial transform once so the compositor has a stable animation origin.
        target.getBoundingClientRect();
        onEnter?.(node, isAppearing);
      }}
      onEntering={(node, isAppearing) => {
        const target = getDialogMotionTarget(node);
        setDialogTransformTransition(target, DIALOG_DURATION.enter);
        target.style.transform = DIALOG_VISIBLE_TRANSFORM;
        onEntering?.(node, isAppearing);
      }}
      onEntered={(node, isAppearing) => {
        clearDialogTransformStyles(getDialogMotionTarget(node));
        onEntered?.(node, isAppearing);
      }}
      onExit={node => {
        const target = getDialogMotionTarget(node);
        target.style.willChange = 'transform';
        setDialogTransformTransition(target, DIALOG_DURATION.exit);
        target.style.transform = DIALOG_HIDDEN_TRANSFORM;
        onExit?.(node);
      }}
      onExited={node => {
        clearDialogTransformStyles(getDialogMotionTarget(node));
        onExited?.(node);
      }}
    />
  );
});

CourseDialogTransition.displayName = 'CourseDialogTransition';

export const CourseManagementMotionProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const history = useHistory();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const activeTransitionRef = useRef<BrowserViewTransition | null>(null);
  const activeAnimationsRef = useRef<Animation[]>([]);
  const temporaryElementsRef = useRef<HTMLElement[]>([]);
  const pendingEntryPathRef = useRef('');
  const generationRef = useRef(0);
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  );
  const reducedMotionRef = useRef(reducedMotion);

  const clearTemporaryStyles = useCallback(() => {
    temporaryElementsRef.current.forEach(element => {
      element.style.willChange = '';
      element.style.removeProperty('view-transition-name');
    });
    temporaryElementsRef.current = [];
    document.documentElement.classList.remove(
      'cm-motion-active',
      'cm-motion-route',
      'cm-motion-layout',
      'cm-motion-presence',
      'cm-motion-results',
    );
  }, []);

  const cancelMotion = useCallback(() => {
    generationRef.current += 1;
    const activeTransition = activeTransitionRef.current;
    activeTransitionRef.current = null;
    try {
      activeTransition?.skipTransition();
    } catch (_error) {
      // A completed native transition may reject a late skip request; cleanup must still finish.
    }
    activeAnimationsRef.current.forEach(animation => animation.cancel());
    activeAnimationsRef.current = [];
    clearTemporaryStyles();
  }, [clearTemporaryStyles]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mediaQuery) {
      return undefined;
    }
    const onChange = (event: MediaQueryListEvent) => {
      reducedMotionRef.current = event.matches;
      setReducedMotion(event.matches);
      if (event.matches) {
        cancelMotion();
      }
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(onChange);
      return () => mediaQuery.removeListener(onChange);
    }
    return undefined;
  }, [cancelMotion]);

  useEffect(() => history.listen((nextLocation, action) => {
    if (action === 'POP' && nextLocation.pathname.startsWith('/course-management')) {
      cancelMotion();
      pendingEntryPathRef.current = nextLocation.pathname;
    }
  }), [cancelMotion, history]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        cancelMotion();
      }
    };
    window.addEventListener('resize', cancelMotion);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('resize', cancelMotion);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      cancelMotion();
    };
  }, [cancelMotion]);

  const runMotion = useCallback((commit: () => void, options: CourseMotionOptions) => {
    cancelMotion();
    const generation = generationRef.current;
    const elements = uniqueElements(options.regions);
    const needsMeasuredElements = options.kind === 'route' || options.kind === 'layout';
    const shouldSkipMotion = reducedMotionRef.current
      || document.visibilityState !== 'visible'
      || (needsMeasuredElements && elements.length === 0);

    if (shouldSkipMotion) {
      commit();
      return;
    }

    const transitionDocument = document as ViewTransitionDocument;
    const useNativeTransition = Boolean(transitionDocument.startViewTransition)
      && (options.kind === 'route' || options.kind === 'layout');

    if (useNativeTransition) {
      const transitionElements = elements.slice(0, MAX_VIEW_TRANSITION_REGIONS);
      temporaryElementsRef.current = transitionElements;
      document.documentElement.classList.add('cm-motion-active', `cm-motion-${options.kind}`);
      transitionElements.forEach((element, index) => {
        element.style.setProperty('view-transition-name', `cm-motion-region-${index}`);
      });
      let committed = false;
      let updateFailed = false;
      let transition: BrowserViewTransition | undefined;
      try {
        transition = transitionDocument.startViewTransition?.(() => {
          committed = true;
          try {
            flushSync(commit);
          } catch (error) {
            updateFailed = true;
            throw error;
          }
        });
      } catch (error) {
        clearTemporaryStyles();
        if (updateFailed) {
          throw error;
        }
        if (!committed) {
          commit();
        }
        return;
      }
      if (!transition) {
        clearTemporaryStyles();
        if (!committed) {
          commit();
        }
        return;
      }
      activeTransitionRef.current = transition;
      transition.finished
        .catch(() => undefined)
        .finally(() => {
          if (generationRef.current !== generation || activeTransitionRef.current !== transition) {
            return;
          }
          activeTransitionRef.current = null;
          clearTemporaryStyles();
        });
      return;
    }

    if (options.kind === 'layout' || options.kind === 'route') {
      const beforeRects = elements.map(element => element.getBoundingClientRect());
      flushSync(commit);
      const afterRects = elements.map(element => element.getBoundingClientRect());
      if (elements.some(element => typeof element.animate !== 'function')) {
        return;
      }
      temporaryElementsRef.current = elements;
      const animations = elements.map((element, index) => {
        const translate = getTranslate(beforeRects[index], afterRects[index]);
        element.style.willChange = 'transform, opacity';
        return element.animate(
          [
            {opacity: 0.9, transform: `translate(${translate.x}px, ${translate.y}px)`},
            {opacity: 1, transform: 'translate(0px, 0px)'},
          ],
          {duration: MOTION_DURATION[options.kind], easing: MOTION_EASING, fill: 'both'},
        );
      });
      activeAnimationsRef.current = animations;
      Promise.all(animations.map(animation => animation.finished.catch(() => undefined))).then(() => {
        if (generationRef.current !== generation || activeAnimationsRef.current !== animations) {
          return;
        }
        animations.forEach(animation => animation.cancel());
        activeAnimationsRef.current = [];
        clearTemporaryStyles();
      });
      return;
    }

    commit();
    window.requestAnimationFrame(() => {
      if (generationRef.current !== generation) {
        return;
      }
      const currentElements = uniqueElements(options.regions);
      if (!currentElements.length || currentElements.some(element => typeof element.animate !== 'function')) {
        return;
      }
      temporaryElementsRef.current = currentElements;
      const distance = options.kind === 'results' ? 4 : 6;
      const animations = currentElements.map(element => {
        element.style.willChange = 'transform, opacity';
        return element.animate(
          [
            {opacity: 0.88, transform: `translateY(${distance}px)`},
            {opacity: 1, transform: 'translateY(0px)'},
          ],
          {duration: MOTION_DURATION[options.kind], easing: MOTION_EASING, fill: 'both'},
        );
      });
      activeAnimationsRef.current = animations;
      Promise.all(animations.map(animation => animation.finished.catch(() => undefined))).then(() => {
        if (generationRef.current !== generation || activeAnimationsRef.current !== animations) {
          return;
        }
        animations.forEach(animation => animation.cancel());
        activeAnimationsRef.current = [];
        clearTemporaryStyles();
      });
    });
  }, [cancelMotion, clearTemporaryStyles]);

  const navigateWithMotion = useCallback((destination: string, options: NavigateOptions = {}) => {
    const commitNavigation = () => {
      if (options.replace) {
        history.replace(destination);
        return;
      }
      history.push(destination);
    };
    if (!isCourseManagementRouteReady(destination)) {
      cancelMotion();
      pendingEntryPathRef.current = destination.split(/[?#]/, 1)[0];
      commitNavigation();
      return;
    }
    runMotion(commitNavigation, {kind: 'route', regions: [surfaceRef]});
  }, [cancelMotion, history, runMotion]);

  const animateRouteEntry = useCallback((pathname: string, region: MotionRegionRef) => {
    if (pendingEntryPathRef.current !== pathname) {
      return;
    }
    pendingEntryPathRef.current = '';
    runMotion(() => undefined, {kind: 'presence', regions: [region]});
  }, [runMotion]);

  const onClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }
    const target = event.target as Element | null;
    const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
    if (!anchor || anchor.target && anchor.target !== '_self' || anchor.hasAttribute('download')) {
      return;
    }
    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin || !destination.pathname.startsWith('/course-management')) {
      return;
    }
    event.preventDefault();
    navigateWithMotion(`${destination.pathname}${destination.search}${destination.hash}`);
  }, [navigateWithMotion]);

  const contextValue = useMemo<CourseManagementMotionContextValue>(() => ({
    animateRouteEntry,
    cancelMotion,
    enabled: true,
    navigateWithMotion,
    reducedMotion,
    runMotion,
  }), [animateRouteEntry, cancelMotion, navigateWithMotion, reducedMotion, runMotion]);

  return (
    <CourseManagementMotionContext.Provider value={contextValue}>
      <div ref={surfaceRef} className="cm-motion-surface" onClickCapture={onClickCapture}>
        {children}
      </div>
    </CourseManagementMotionContext.Provider>
  );
};

export const useCourseManagementMotion = () => useContext(CourseManagementMotionContext);

export const useCourseDialogMotionProps = () => {
  const {reducedMotion} = useCourseManagementMotion();
  return useMemo(() => ({
    TransitionComponent: reducedMotion ? Fade : CourseDialogTransition,
    transitionDuration: reducedMotion ? 0 : DIALOG_DURATION,
    TransitionProps: {
      easing: MOTION_EASING,
    },
    slotProps: {
      backdrop: {
        transitionDuration: reducedMotion ? 0 : DIALOG_BACKDROP_DURATION,
      },
    },
  }), [reducedMotion]);
};

export const CourseManagementRouteEntry: React.FC<{children: React.ReactNode}> = ({children}) => {
  const location = useLocation();
  const regionRef = useRef<HTMLDivElement | null>(null);
  const {animateRouteEntry} = useCourseManagementMotion();

  useEffect(() => {
    animateRouteEntry(location.pathname, regionRef);
  }, [animateRouteEntry, location.pathname]);

  return <div ref={regionRef} className="cm-motion-route-entry">{children}</div>;
};

export const useCourseManagementNavigate = () => {
  const history = useHistory();
  const {enabled, navigateWithMotion} = useCourseManagementMotion();
  return useCallback((destination: string, options: NavigateOptions = {}) => {
    if (enabled) {
      navigateWithMotion(destination, options);
      return;
    }
    if (options.replace) {
      history.replace(destination);
      return;
    }
    history.push(destination);
  }, [enabled, history, navigateWithMotion]);
};
